<template>
  <div>
    <template v-if="locations.length > 0">
      <select v-model="locationChoice" class="form-control" :class="{ 'm-b-xs': showLocationFreeText }" data-cy="event-location-select">
        <option :value="null">No location</option>
        <option v-for="location in locations" :key="location.id" :value="location.id">{{ locationLabel(location) }}</option>
        <option value="other">Other...</option>
      </select>
      <input v-if="showLocationFreeText" v-model="freeText" type="text" class="form-control" placeholder="Enter a location">
    </template>
    <input v-else-if="locationsLoaded" v-model="freeText" type="text" class="form-control">
    <p v-if="zoneUnknown" class="text-14 text-red-600 m-t-xs m-b-none" data-cy="event-location-load-error">
      We couldn't load your locations, so this event's time zone isn't known. Refresh the page before saving.
    </p>
  </div>
</template>

<script>
// Shared structured-location picker: a dropdown of the team's Location records
// with "No location" and a free-text "Other..." escape hatch. Used by the event
// wizard and the event edit form. Emits a single `change` event with
// { locationId, location, locationName, timeZone, ready } whenever either value
// changes; the parent owns the values that end up in the API payload.
// timeZone is the selected location's own IANA zone (null when it follows the
// team, or for free text), and ready is true once timeZone can be trusted: the
// list loaded and actually contains the selected location. A failed load is
// never ready, so forms don't read times on the wrong clock.
export default {
  name: 'location-select',
  props: {
    // For edit flows these arrive async (after the parent fetches the event),
    // so both are watched rather than only read at mount.
    initialLocationId: {
      type: Number,
      required: false,
      default: null,
    },
    initialLocation: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      locations: [],
      // Tracks whether getLocations() has finished so we don't flash the
      // free-text fallback while the request is still in flight.
      locationsLoaded: false,
      loadFailed: false,
      // Drives the <select>: a location id, null ("No location"), or 'other'
      // (reveal the free-text input for a one-off venue).
      locationChoice: null,
      showLocationFreeText: false,
      freeText: '',
    };
  },
  computed: {
    // A structured location is selected (an edit or reused event) but the list
    // that carries its zone failed to load
    zoneUnknown() {
      return this.loadFailed && this.locationChoice !== null && this.locationChoice !== 'other';
    },
  },
  created() {
    this.getLocations();
    this.applyInitialValues();
  },
  watch: {
    initialLocationId() {
      this.applyInitialValues();
    },
    initialLocation() {
      this.applyInitialValues();
    },
    locationChoice(val) {
      const vm = this;
      if (val === 'other') {
        vm.showLocationFreeText = true;
      } else {
        vm.showLocationFreeText = false;
        vm.freeText = '';
      }
      vm.emitChange();
    },
    freeText() {
      this.emitChange();
    },
  },
  methods: {
    applyInitialValues() {
      const vm = this;
      // Prefer the structured location_id; fall back to the free-text
      // ("Other...") mode when only a string is set. Both empty resets the
      // selection — the wizard clears its initial* props when starting over.
      if (vm.initialLocationId) {
        vm.locationChoice = vm.initialLocationId;
      } else if (vm.initialLocation) {
        vm.locationChoice = 'other';
        vm.freeText = vm.initialLocation;
      } else {
        vm.locationChoice = null;
        vm.freeText = '';
      }
    },
    emitChange() {
      const vm = this;
      const locationId = (vm.locationChoice === 'other' || vm.locationChoice === null) ? null : vm.locationChoice;
      // Teams with no Location records only see the free-text input, so the
      // typed value counts as the location even though locationChoice stays null.
      const usingFreeText = vm.locationChoice === 'other' || (vm.locationsLoaded && vm.locations.length === 0);
      // The parent payload only carries an id/free-text string, so also hand up
      // the selected structured location's name for display (e.g. review screens).
      const selected = locationId ? vm.locations.find((l) => l.id === locationId) : null;
      vm.$emit('change', {
        locationId,
        location: usingFreeText ? vm.freeText : '',
        locationName: selected ? selected.name : '',
        timeZone: selected ? (selected.time_zone || null) : null,
        ready: vm.locationsLoaded && (!locationId || !!selected),
      });
    },
    locationLabel(location) {
      // Show the name plus a short address hint so coaches can tell apart
      // similarly-named locations, e.g. "Main Gym — 123 Oak St, Springfield, IL 62704".
      // State and zip read as "IL 62704" (no comma), matching Event#display_location.
      const stateZip = [location.state, location.postal_code].filter(Boolean).join(' ');
      const address = [location.address_line1, location.address_line2, location.city, stateZip]
        .filter(Boolean)
        .join(', ');
      return address ? `${location.name} — ${address}` : location.name;
    },
    getLocations() {
      const vm = this;
      // Include archived locations so an event tied to a since-archived location
      // still has a matching <option> and keeps its current selection on edit.
      const url = `${vm.$apiService.locationsUrl()}?include_archived=true`;
      vm.$apiService.loadAllPages(url, 'locations')
        .then((locations) => {
          vm.locations = locations;
        })
        .catch(() => {
          // Non-fatal for the location itself: with no locations loaded the
          // component falls back to the free-text input. The time zone is
          // another matter, hence the error and ready staying false.
          vm.locations = [];
          vm.loadFailed = true;
        })
        .finally(() => {
          vm.locationsLoaded = true;
          // Initial values may have been applied before the list loaded, so the
          // first emit couldn't resolve a name. Re-emit now that names exist.
          vm.emitChange();
        });
    },
  },
};
</script>
